/**
 * Session Provider
 * Provides session management context to the entire application
 */
import React, { createContext, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import sessionManager from '../services/sessionManager';
import store from '../store';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';
import SessionStatus from '../components/SessionStatus';

const SessionContext = createContext();

export const useSession = () => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};

export const SessionProvider = ({ children, showTimeoutWarning = true, showSessionStatus = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, accessToken } = useSelector((state) => state.auth);

    // List of public routes that don't require authentication
    const publicRoutes = [
        '/',
        '/login',
        '/signup',
        '/otp-signup',
        '/verify-otp',
        '/book-appointment',
        '/doctor-onboarding',
        '/enhanced-doctor-onboarding',
        '/welcome',
        '/doctor',
        '/auth/magic-link',
        '/auth/verify-email',
        '/auth/forgot-password',
        '/auth/reset-password',
        '/onboarding/patient',
        '/onboarding/patient-unified'
    ];

    // Check if current route is public
    const isPublicRoute = publicRoutes.some(route => 
        location.pathname === route || 
        location.pathname.startsWith(route + '/') || 
        location.pathname.startsWith('/doctor/')
    );

    // Initialize session management
    useEffect(() => {
        // Set the store on sessionManager
        sessionManager.setStore(store);

        // Cleanup on unmount
        return () => {
            sessionManager.destroy();
        };
    }, []);

    // Handle authentication state changes - only manage session for authenticated users
    // DO NOT redirect public routes - let them work without authentication
    useEffect(() => {
        if (user && accessToken) {
            // User is authenticated, start session management
            sessionManager.startSessionManagement();
        } else if (!isPublicRoute) {
            // Only redirect to login if we're on a protected route and user is not authenticated
            // Don't redirect if we're already on login/signup pages
            if (location.pathname !== '/login' && location.pathname !== '/signup' && location.pathname !== '/otp-signup') {
                // This will be handled by ProtectedRoute components for protected routes
                // Don't force redirect here to avoid breaking public routes
            }
        }
    }, [user, accessToken, location.pathname, isPublicRoute]);

    // Provide session context
    const contextValue = {
        sessionManager,
        isAuthenticated: !!(user && accessToken),
        user,
        accessToken
    };

    return ( <
        SessionContext.Provider value = { contextValue } > { children }

        { /* Session timeout warning */ } { showTimeoutWarning && < SessionTimeoutWarning / > }

        { /* Session status (optional) */ } {
            showSessionStatus && ( <
                Box position = "fixed"
                bottom = { 16 }
                right = { 16 }
                zIndex = { 9999 } >
                <
                SessionStatus compact / >
                <
                /Box>
            )
        } <
        /SessionContext.Provider>
    );
};

export default SessionProvider;